import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { Renditebruecke } from "../../lib/otterfolio";
import type { OverridesForm } from "./RenditebrueckeEditModal";
import { RenditebrueckeEditModal } from "./RenditebrueckeEditModal";
import { OTTERFOLIO_THEME } from "./theme";

interface Props {
  rb: Renditebruecke | null;
  overrides: OverridesForm;
  onSave: (v: OverridesForm) => Promise<void>;
  renditebrueckeUpdatedAt: string | null;
}

function fmt(pct: number): string {
  return `${pct >= 0 ? "+" : ""}${pct.toFixed(1)} %`;
}

export function RenditebrueckeCard({ rb, overrides, onSave, renditebrueckeUpdatedAt }: Props) {
  const { t } = useTranslation();
  const [editOpen, setEditOpen] = useState(false);
  if (!rb) return null;
  const overColor = rb.overHurdle ? OTTERFOLIO_THEME.primary : OTTERFOLIO_THEME.highlight;
  return (
    <div
      style={{
        background: OTTERFOLIO_THEME.cardBg,
        border: `1px solid ${OTTERFOLIO_THEME.cardBorder}`,
        borderRadius: 12,
        padding: 20,
      }}
    >
      <header
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: 8,
        }}
      >
        <h3 style={{ margin: 0, color: OTTERFOLIO_THEME.text }}>
          {t("otterfolio.renditebruecke.label", { defaultValue: "Renditebrücke" })}
        </h3>
        <button type="button" onClick={() => setEditOpen(true)}>
          {t("otterfolio.renditebruecke.editButton", { defaultValue: "Bearbeiten" })}
        </button>
      </header>
      <div style={{ fontSize: 36, fontWeight: 800, color: overColor }}>
        {fmt(rb.bruecke)} {rb.overHurdle ? "🟢" : "🟡"}
        {rb.stale ? " ⚠️" : ""}
      </div>
      <div style={{ fontSize: 12, color: OTTERFOLIO_THEME.textMuted }}>
        {t("otterfolio.renditebruecke.hurdle", { defaultValue: "Hürde" })}: {rb.hurdle.toFixed(1)} %
      </div>
      <ul
        style={{
          marginTop: 12,
          listStyle: "none",
          padding: 0,
          display: "flex",
          gap: 16,
          flexWrap: "wrap",
          fontSize: 12,
          color: OTTERFOLIO_THEME.text,
        }}
      >
        <li>
          {t("otterfolio.renditebruecke.wachstum", { defaultValue: "Gewinnwachstum" })}:{" "}
          <strong>{fmt(rb.breakdown.wachstum)}</strong>
        </li>
        <li>
          {t("otterfolio.renditebruecke.ausschuettung", { defaultValue: "Ausschüttung" })}:{" "}
          <strong>{fmt(rb.breakdown.ausschuettung)}</strong>
        </li>
        <li>
          {t("otterfolio.renditebruecke.bewertung", { defaultValue: "Bewertung" })}:{" "}
          <strong>{fmt(rb.breakdown.bewertung)}</strong>
        </li>
      </ul>
      {rb.stale && (
        <p style={{ color: OTTERFOLIO_THEME.highlight, fontSize: 12, marginTop: 8 }}>
          {t("otterfolio.renditebruecke.stale", {
            defaultValue: "Zuletzt vor > 45 Tagen eingeschätzt — bitte hinterfragen",
          })}
        </p>
      )}
      {renditebrueckeUpdatedAt && (
        <p style={{ color: OTTERFOLIO_THEME.textMuted, fontSize: 11, marginTop: 4 }}>
          {t("otterfolio.renditebruecke.lastUpdate", {
            defaultValue: "Zuletzt eingeschätzt: {{date}}",
            date: renditebrueckeUpdatedAt.slice(0, 10),
          })}
        </p>
      )}
      <RenditebrueckeEditModal
        open={editOpen}
        initial={overrides}
        onClose={() => setEditOpen(false)}
        onSave={onSave}
      />
    </div>
  );
}
